import React, { useMemo } from 'react';
import ConditionalRender from '@Components/ConditionalRender/ConditionalRender';
import { isPlanIncluded } from '../util';
import DeviceDelivery from './../components/DeviceDelivery';
import DigitalDelivery from './../components/DigitalDelivery';
import ProvidedDocument from './../components/ProvidedDocument';
import PickupOutletDelivery from './../components/PickupOutletDelivery';
import ContactPersonalDetails from './../components/ContactPersonalDetails';
import PrepaidProductDelivery from './../components/PrepaidProductDelivery';
import DeliveryDetailsHBBWFBB from './../components/DeliveryDetailsHBBWFBB';
import ProvidedDocumentHBBWFBB from './../components/ProvidedDocumentHBBWFBB';
import { BUY_HBB, BUY_WFBB } from '@Views/Map/Map';
import { useCart } from '@Utils/context/CartContext';
import { IAddress } from '@ApiModels/commerceProfile';
import { AccordionsContainer } from '../ShippingDetails.styled';
import { ICheckout } from '@Redux/modules/checkout/actions';

interface IHomeDelivery {
	contactDetails: ICheckout['payload']['contactDetails'];
	getTitle: (address: IAddress) => string;
	hasDevice: boolean;
	hasGiftCards: boolean;
	isSimplifiedCheckout: boolean;
	launchDate: string;
	orderIncludesPrepaid: boolean;
	pickupDelivery: boolean | undefined;
	pickUpOutlet: boolean;
	selectedAddress: IAddress | undefined;
}

const HomeDelivery = ({
	contactDetails,
	getTitle,
	hasDevice,
	hasGiftCards,
	isSimplifiedCheckout,
	launchDate,
	orderIncludesPrepaid,
	pickupDelivery,
	pickUpOutlet,
	selectedAddress,
}: IHomeDelivery): JSX.Element => {
	const { cart, cartItemsLength } = useCart();
	const orderIncludesPlan = isPlanIncluded(cart.items);
	const otherDeviceIncart = cart.items.length > 1;
	const preorderDate = cart?.items[0]?.plan?.preorderDate;
	const hbbWfbbItem = useMemo(() => {
		return cart.items.find((item) => [BUY_HBB, BUY_WFBB].includes(item.plan?.PA || ''));
	}, [cart]);
	const addressAvailable = selectedAddress?.wilayat !== '' || selectedAddress?.way !== '';

	return (
		<>
			<AccordionsContainer>
				<ConditionalRender
					show={!!cartItemsLength && !isSimplifiedCheckout}
					onTrue={() => (
						<ContactPersonalDetails
							contactDetails={contactDetails}
							cartItemsLength={cartItemsLength}
							hbbWfbbItem={hbbWfbbItem}
							otherDeviceIncart={otherDeviceIncart}
							orderIncludesPlan={orderIncludesPlan}
							orderWithPrepaid={orderIncludesPrepaid}
							showMap={!isSimplifiedCheckout}
							selectedAddress={selectedAddress}
						/>
					)}
				/>
				<ConditionalRender show={!!hbbWfbbItem} onTrue={() => <ProvidedDocumentHBBWFBB />} />
				<ConditionalRender
					show={!!orderIncludesPrepaid}
					onTrue={() => <ProvidedDocument orderWithPrepaid={orderIncludesPrepaid} />}
				/>
				<ConditionalRender
					show={orderIncludesPrepaid}
					onTrue={() => (
						<PrepaidProductDelivery
							selectedAddress={selectedAddress}
							subtitle={selectedAddress ? getTitle(selectedAddress) : ''}
							orderWithPrepaid={orderIncludesPrepaid}
							showMap={!isSimplifiedCheckout}
						/>
					)}
				/>
				<ConditionalRender
					show={!!hbbWfbbItem}
					onTrue={() => <DeliveryDetailsHBBWFBB showMap={!isSimplifiedCheckout} />}
				/>
				<ConditionalRender
					show={hasDevice && !pickUpOutlet && (!isSimplifiedCheckout || addressAvailable)}
					onTrue={() => (
						<DeviceDelivery
							launchDate={launchDate}
							pickupDelivery={pickupDelivery}
							preorderDate={preorderDate}
							selectedAddress={selectedAddress}
							showMap={!isSimplifiedCheckout}
						/>
					)}
				/>
				<ConditionalRender
					show={pickUpOutlet && !isSimplifiedCheckout}
					onTrue={() => (
						<PickupOutletDelivery
							pickupDelivery={pickupDelivery}
							preorderDate={preorderDate}
							showMap={!isSimplifiedCheckout}
							selectedAddress={selectedAddress}
						/>
					)}
				/>
				<ConditionalRender
					show={hasGiftCards && !!contactDetails?.email}
					onTrue={() => <DigitalDelivery subtitle={contactDetails?.email ?? ''} />}
				/>
			</AccordionsContainer>
		</>
	);
};

export default HomeDelivery;
